import { useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import * as Yup from "yup";
import Flex from "@/components/Flex";
import MaskMultiSelect from "@/components/MaskMultiSelect";
import StyledButtonV2 from "@/components/app/StyledButtonV2";
import StyledFormModal from "@/components/globalModal/StyledModal";
import useSelectedPatient from "@/hooks/local-storage/use-selected-patient";
import { DisclosureHost } from "@/hooks/use-disclosure";
import { useSnackbar } from "@/hooks/useSnackbar";
import { useSendPatientForms } from "@/queries/patient-chart/forms/use-send-patient-forms";
import { showToastOnApiCall, getXtenantId } from "@/utils/utils";
import { usePatientFormOptions } from "@/hooks/use-patient-form-options";
import useCurrentUser from "@/hooks/local-storage/use-current-user";

interface SendPatientFormsModalProps {
  disclosureHost: DisclosureHost;
}

const validationSchema = Yup.object({
  // formType: Yup.string().required("Form type is required"),
  selectedForms: Yup.array().min(1, "Please select at least one form"),
});

const SendPatientFormsModal = ({
  disclosureHost,
}: SendPatientFormsModalProps) => {
  const showSnackbar = useSnackbar();
  const [selectedPatient] = useSelectedPatient();
  const [me] = useCurrentUser();
  const queryClient = useQueryClient();
  const formOptions = usePatientFormOptions();

  const { mutateAsync: sendPatientForms } = useSendPatientForms();

  const handleSendPatientForms = (v: any) => {
    const patientId = selectedPatient?.uuid;

    if (!patientId) return;

    const payload = {
      patientForms: v.selectedForms?.map((v: any) => v.label),
      patientId,
      sendBy: me?.firstName + " " + me?.lastName,
      xTENANTID: getXtenantId(),
    };

    showToastOnApiCall({
      apiCall: sendPatientForms(payload),
      onSave: () => {
        queryClient.invalidateQueries({
          queryKey: ["patient-chart-forms-list"],
        });
        handleClose();
      },
      showSnackbar: showSnackbar,
    });
  };
  // const { mutateAsync: saveDraft, isPending: isSavingDraft } =
  //   useSavePatientFormsDraft();
  // const saveDraft = (v: any) => {};

  const formik = useFormik<{ selectedForms?: any[] }>({
    initialValues: { selectedForms: [] },
    validationSchema,
    onSubmit: async (values) => handleSendPatientForms(values),
  });

  function handleClose() {
    formik.resetForm();
    disclosureHost.close();
  }

  // const handleSaveAsDraft = async () => {
  //   try {
  //     await saveDraft({
  //       patientUuid: selectedPatient?.uuid!,
  //       selectedForms: formik.values.selectedForms,
  //       xTENANTID: getXtenantId(),
  //     });
  //     showSnackbar(getSuccessSnackbar("Draft saved successfully"));
  //     handleClose();
  //   } catch (error) {
  //     showSnackbar(getErrorSnackbar(error));
  //   }
  // };

  return (
    <StyledFormModal
      open={disclosureHost.opened}
      onClose={handleClose}
      dialogTitle="Send Patient Forms"
      dialogAction={
        <Flex justifyContent="flex-end" gap={2}>
          <StyledButtonV2
            label="Cancel"
            variant="outlined"
            onClick={handleClose}
          />
          {/* <StyledButtonV2
              label="Save as Draft"
              onClick={handleSaveAsDraft}
              variant="outlined"
              // disabled={isSavingDraft}
              style={{
                backgroundColor: "#E3F2FD",
                borderColor: "#1976D2",
                color: "#1976D2",
              }}
            /> */}
          <StyledButtonV2
            label="Send"
            onClick={() => formik.submitForm()}
            variant="contained"
            // disabled={!formik.isValid}
          />
        </Flex>
      }
      sx={{
        "& .MuiDialog-paper": {
          width: "420px",
          height: "220px",
        },
      }}
    >
      <Flex
        onClick={(e) => e.stopPropagation()}
        flexDirection={"column"}
        gap={2}
        width={"100%"}
        pt={2}
      >
        <MaskMultiSelect
          required
          placeholder="Select Forms"
          options={formOptions}
          value={formik.values.selectedForms}
          onChange={(values) => {
            formik.setFieldValue("selectedForms", values);
          }}
          error={
            formik.touched.selectedForms
              ? (formik.errors.selectedForms as string)
              : undefined
          }
          limitTags={1}
        />
      </Flex>
    </StyledFormModal>
  );
};

export default SendPatientFormsModal;
